import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  CheckCircle2,
  Headphones,
  Loader2,
  MessageSquare,
  Package,
  RefreshCw,
  Search,
  Shield,
  ShoppingCart,
  TrendingUp,
  User,
  XCircle,
} from "lucide-react";
import { ConsoleShell } from "@/components/ConsoleShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "leads", label: "Hot leads" },
  { id: "stock", label: "Stock queue" },
  { id: "support", label: "Support" },
  { id: "orders", label: "Orders" },
  { id: "inquiries", label: "Inquiries" },
  { id: "catalog", label: "Parts catalog" },
];

const CATALOG_CATEGORIES = [
  "",
  "filters",
  "brakes",
  "oil",
  "battery",
  "ignition",
  "cooling",
  "suspension",
  "tires",
  "lighting",
  "electrical",
  "engine",
  "drivetrain",
];

function IntentBadge({ intent }) {
  if (!intent) return null;
  const colors = {
    ready_to_buy: "border-cyan-electric text-cyan-electric bg-cyan-electric/10",
    qualified: "border-mclaren text-mclaren bg-mclaren/10",
    interested: "border-white/30 text-white/60",
    browsing: "border-white/15 text-white/40",
    support: "border-yellow-400/50 text-yellow-300 bg-yellow-400/5",
    purchased: "border-green-400/50 text-green-300",
  };
  const cls = colors[intent.label] || colors.browsing;
  return (
    <span className={`inline-block px-2 py-0.5 font-mono-tech text-[9px] uppercase tracking-widest border ${cls}`}>
      {intent.label?.replace(/_/g, " ")} · {intent.score}%
    </span>
  );
}

export default function SalesDesk() {
  const { user } = useAuth();
  const [tab, setTab] = useState("leads");
  const [dash, setDash] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmNote, setConfirmNote] = useState("");
  const [catalogQuery, setCatalogQuery] = useState("");
  const [catalogCategory, setCatalogCategory] = useState("");
  const [catalogResults, setCatalogResults] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogSelected, setCatalogSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [data, inq] = await Promise.all([
        axios.get(`${API}/marketplace/sales/dashboard`).then((r) => r.data),
        axios.get(`${API}/marketplace/sales/inquiries`).then((r) => r.data).catch(() => []),
      ]);
      setDash(data);
      setInquiries(inq || []);
    } catch {
      setDash(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async (sessionId) => {
    if (!sessionId) {
      setDetail(null);
      return;
    }
    setDetailLoading(true);
    try {
      const { data } = await axios.get(`${API}/marketplace/sales/sessions/${sessionId}`);
      setDetail(data);
    } catch {
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (selected?.session_id) {
      loadDetail(selected.session_id);
    } else if (selected?.order) {
      setDetail({
        customer_full: selected.customer || {},
        order: selected.order,
        listing: null,
        messages: [],
        user: selected.user,
      });
      setDetailLoading(false);
    } else {
      setDetail(null);
    }
  }, [selected, loadDetail]);

  const confirmStock = async (orderId, inStock) => {
    setBusy(true);
    try {
      await axios.post(`${API}/marketplace/sales/orders/${orderId}/confirm`, {
        in_stock: inStock,
        note: confirmNote || (inStock ? "Confirmed by sales desk." : "Not available."),
      });
      setConfirmNote("");
      await load();
      if (selected?.session_id) await loadDetail(selected.session_id);
    } finally {
      setBusy(false);
    }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!selected?.session_id || reply.trim().length < 2) return;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/marketplace/sales/sessions/${selected.session_id}/reply`,
        { message: reply.trim() },
      );
      setDetail(data);
      setReply("");
      await load();
    } finally {
      setBusy(false);
    }
  };

  const searchCatalog = useCallback(async (query = catalogQuery, category = catalogCategory) => {
    setCatalogLoading(true);
    try {
      const params = new URLSearchParams();
      if (query.trim()) params.set("q", query.trim());
      if (category) params.set("category", category);
      params.set("limit", "40");
      const { data } = await axios.get(`${API}/marketplace/sales/catalog?${params.toString()}`);
      setCatalogResults(data.listings || []);
    } catch {
      setCatalogResults([]);
    } finally {
      setCatalogLoading(false);
    }
  }, [catalogQuery, catalogCategory]);

  useEffect(() => {
    if (tab === "catalog") {
      searchCatalog(catalogQuery, catalogCategory);
    }
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const notifyCustomerAboutPart = async (listing, inStock) => {
    if (!selected?.session_id) return;
    const price =
      listing.price_usd > 0 ? `$${listing.price_usd}` : "quote on request";
    const msg = inStock
      ? `Good news - we have ${listing.name} (${listing.sku}) in stock. ${listing.availability_label}. Price: ${price}. Let me know if you'd like to proceed.`
      : `${listing.name} (${listing.sku}) is not available right now (${listing.availability_label}). I can suggest an alternative if you'd like.`;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/marketplace/sales/sessions/${selected.session_id}/reply`,
        { message: msg },
      );
      setDetail(data);
      if (selected.session_id) await loadDetail(selected.session_id);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const stats = dash?.stats || {};
  const inquiryList =
    tab === "inquiries"
      ? (inquiries || []).map((inq) => ({
          session_id: inq.id,
          customer: { name: inq.contact_name, part_query: inq.message },
          intent: { label: "interested", score: 40 },
          stage: inq.status,
          buyer_ready: false,
          part_name: inq.message?.slice(0, 60),
          updated_at: inq.created_at,
        }))
      : [];

  const list =
    tab === "inquiries"
      ? inquiryList
      : tab === "leads"
      ? dash?.hot_leads || []
      : tab === "stock"
        ? (dash?.stock_queue || []).map((o) => ({
            session_id: o.session_id,
            order_id: o.id,
            customer: o.customer_details || { name: o.contact_name, phone: o.phone },
            intent: { label: "qualified", score: 85 },
            stage: o.status,
            listing_sku: o.sku,
            buyer_ready: true,
            user: o.user,
            part_name: o.part_name,
            order: o,
            updated_at: o.created_at,
          }))
        : tab === "support"
          ? dash?.support_queue || []
          : (dash?.recent_orders || []).map((o) => ({
              order_id: o.id,
              session_id: o.session_id,
              customer: o.customer_details || { name: o.contact_name, phone: o.phone },
              intent: { label: o.status === "completed" ? "purchased" : "qualified", score: 80 },
              stage: o.status,
              listing_sku: o.sku,
              buyer_ready: o.status !== "completed",
              user: o.user,
              part_name: o.part_name,
              order: o,
            }));

  return (
    <ConsoleShell title="sales desk">
      <div className="mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-10 py-6 pb-20">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <span className="chip-brand">/ sales team portal</span>
            <h1 className="mt-3 font-display text-3xl sm:text-4xl font-semibold tracking-tighter flex items-center gap-3">
              <Headphones className="h-8 w-8 text-cyan-electric" />
              Sales Desk
            </h1>
            <p className="mt-2 text-sm text-white/50 max-w-xl">
              Live buyer leads from the marketplace agent - confirm stock, reply in chat, close orders.
              {user?.role === "admin" && " You also have access to the full Admin platform."}
            </p>
          </div>
          <div className="flex gap-2">
            {user?.role === "admin" && (
              <Link
                to="/console/admin"
                className="h-10 px-4 border border-mclaren/40 text-mclaren font-mono-tech text-[10px] uppercase flex items-center gap-2 hover:bg-mclaren/10"
              >
                <Shield className="h-4 w-4" /> Admin platform
              </Link>
            )}
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="h-10 px-4 border border-white/15 font-mono-tech text-[10px] uppercase flex items-center gap-2 hover:border-cyan-electric"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          {[
            { label: "Hot leads", val: stats.hot_leads, icon: TrendingUp },
            { label: "Stock queue", val: stats.pending_stock, icon: ShoppingCart },
            { label: "Support", val: stats.support_waiting, icon: MessageSquare },
            { label: "Active chats", val: stats.active_sessions, icon: User },
            { label: "Sold today", val: stats.completed_today, icon: CheckCircle2 },
          ].map(({ label, val, icon: Icon }) => (
            <div key={label} className="border border-white/10 bg-obsidian/60 p-4">
              <Icon className="h-4 w-4 text-cyan-electric mb-2" />
              <div className="font-display text-2xl">{val ?? "-"}</div>
              <div className="font-mono-tech text-[9px] uppercase text-white/40">{label}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`h-9 px-4 font-mono-tech text-[10px] uppercase tracking-widest border ${
                tab === t.id ? "border-cyan-electric text-cyan-electric" : "border-white/10 text-white/50"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "catalog" && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 min-h-[520px] mb-6">
            <div className="lg:col-span-5 border border-white/10 bg-obsidian/40 flex flex-col max-h-[75vh]">
              <div className="p-4 border-b border-white/10 space-y-3">
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-cyan-electric" />
                  <span className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">
                    Search parts catalog
                  </span>
                </div>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    searchCatalog();
                  }}
                  className="flex gap-2"
                >
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/30" />
                    <input
                      value={catalogQuery}
                      onChange={(e) => setCatalogQuery(e.target.value)}
                      placeholder="SKU, part name, make, model…"
                      className="w-full h-10 bg-black/50 border border-white/15 pl-9 pr-3 text-sm"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={catalogLoading}
                    className="h-10 px-4 bg-mclaren text-void font-mono-tech text-[10px] uppercase shrink-0"
                  >
                    Search
                  </button>
                </form>
                <select
                  value={catalogCategory}
                  onChange={(e) => {
                    setCatalogCategory(e.target.value);
                    searchCatalog(catalogQuery, e.target.value);
                  }}
                  className="w-full h-9 bg-black/50 border border-white/15 px-3 text-sm font-mono-tech"
                >
                  {CATALOG_CATEGORIES.map((c) => (
                    <option key={c || "all"} value={c}>
                      {c ? c.replace(/_/g, " ") : "All categories"}
                    </option>
                  ))}
                </select>
                {selected?.session_id && (
                  <div className="text-xs text-cyan-electric/80 border border-cyan-electric/20 bg-cyan-electric/5 px-3 py-2">
                    Customer linked:{" "}
                    <span className="text-white/80">
                      {selected.customer?.name || selected.user?.name || "Customer"}
                    </span>
                    {" - "}confirm stock below to message them in chat.
                  </div>
                )}
                {!selected?.session_id && (
                  <div className="text-xs text-white/40 px-1">
                    Tip: select a customer from Hot leads or Support first, then confirm stock here.
                  </div>
                )}
              </div>
              <div className="flex-1 overflow-y-auto divide-y divide-white/5">
                {catalogLoading && (
                  <div className="p-8 flex justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-cyan-electric" />
                  </div>
                )}
                {!catalogLoading && catalogResults.length === 0 && (
                  <div className="p-8 text-sm text-white/40">No parts found. Try another search.</div>
                )}
                {catalogResults.map((item) => (
                  <button
                    key={item.id || item.sku}
                    type="button"
                    onClick={() => setCatalogSelected(item)}
                    className={`w-full text-left p-4 hover:bg-white/5 ${
                      catalogSelected?.sku === item.sku ? "bg-cyan-electric/5 border-l-2 border-cyan-electric" : ""
                    }`}
                  >
                    <div className="font-mono-tech text-[9px] uppercase text-cyan-electric">{item.sku}</div>
                    <div className="font-display text-base mt-0.5">{item.name}</div>
                    <div className="mt-1 flex flex-wrap gap-2 text-xs text-white/45">
                      <span>{item.category}</span>
                      <span>·</span>
                      <span>{item.price_usd > 0 ? `$${item.price_usd}` : "quote"}</span>
                    </div>
                    <div
                      className={`mt-1 font-mono-tech text-[9px] uppercase ${
                        item.availability === "in_stock" || item.availability === "low"
                          ? "text-green-400"
                          : item.availability === "special_order"
                            ? "text-yellow-400"
                            : "text-white/40"
                      }`}
                    >
                      {item.availability_label}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="lg:col-span-7 border border-white/10 bg-obsidian/60 flex flex-col max-h-[75vh]">
              {!catalogSelected && (
                <div className="flex-1 flex items-center justify-center text-white/35 text-sm p-8 text-center">
                  Search and select a part to view details and confirm stock with a customer.
                </div>
              )}
              {catalogSelected && (
                <>
                  <div className="px-5 py-4 border-b border-white/10">
                    <div className="font-mono-tech text-[10px] uppercase text-cyan-electric">
                      {catalogSelected.sku} · {catalogSelected.category} · {catalogSelected.type}
                    </div>
                    <div className="font-display text-2xl mt-1">{catalogSelected.name}</div>
                    <div className="font-display text-2xl text-cyan-electric mt-2">
                      {catalogSelected.price_usd > 0 ? `$${catalogSelected.price_usd}` : "Quote required"}
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto p-5 space-y-4">
                    <p className="text-sm text-white/60">{catalogSelected.description}</p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div className="border border-white/10 p-3">
                        <div className="font-mono-tech text-[9px] uppercase text-white/40 mb-1">Availability</div>
                        <div
                          className={`text-sm font-mono-tech uppercase ${
                            catalogSelected.availability === "in_stock" || catalogSelected.availability === "low"
                              ? "text-green-400"
                              : "text-yellow-300"
                          }`}
                        >
                          {catalogSelected.availability_label}
                        </div>
                      </div>
                      <div className="border border-white/10 p-3">
                        <div className="font-mono-tech text-[9px] uppercase text-white/40 mb-1">Lead time</div>
                        <div className="text-sm">{catalogSelected.lead_days || 1} day(s)</div>
                      </div>
                      <div className="border border-white/10 p-3 sm:col-span-2">
                        <div className="font-mono-tech text-[9px] uppercase text-white/40 mb-1">Fits</div>
                        <div className="text-sm text-white/70">
                          {(catalogSelected.fits_brands || []).join(" · ")}
                        </div>
                        <div className="text-xs text-white/45 mt-1">{catalogSelected.fits_notes}</div>
                      </div>
                    </div>

                    <div className="border border-mclaren/40 bg-mclaren/5 p-4 space-y-3">
                      <div className="font-mono-tech text-[10px] uppercase text-mclaren">
                        Confirm with customer
                      </div>
                      {!selected?.session_id ? (
                        <p className="text-sm text-white/50">
                          Select a customer from Hot leads, Support, or Stock queue - then return here to send them
                          stock confirmation in their agent chat.
                        </p>
                      ) : (
                        <>
                          <p className="text-sm text-white/60">
                            Send stock status to{" "}
                            <span className="text-white">
                              {selected.customer?.name || selected.user?.name || "customer"}
                            </span>{" "}
                            in their live chat.
                          </p>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              disabled={busy || (catalogSelected.stock <= 0 && catalogSelected.price_usd > 0)}
                              onClick={() => notifyCustomerAboutPart(catalogSelected, true)}
                              className="flex-1 h-10 bg-cyan-electric text-void font-mono-tech text-[10px] uppercase flex items-center justify-center gap-1 disabled:opacity-40"
                            >
                              <CheckCircle2 className="h-4 w-4" /> Confirm in stock
                            </button>
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => notifyCustomerAboutPart(catalogSelected, false)}
                              className="flex-1 h-10 border border-white/20 font-mono-tech text-[10px] uppercase flex items-center justify-center gap-1"
                            >
                              <XCircle className="h-4 w-4" /> Not available
                            </button>
                          </div>
                          {(detail?.order?.status === "pending_sales_review" ||
                            selected.stage === "pending_sales_review") &&
                            detail?.listing?.sku === catalogSelected.sku && (
                              <div className="pt-2 border-t border-white/10 space-y-2">
                                <div className="font-mono-tech text-[9px] uppercase text-white/40">
                                  Also update pending order
                                </div>
                                <input
                                  value={confirmNote}
                                  onChange={(e) => setConfirmNote(e.target.value)}
                                  placeholder="Note to customer (optional)"
                                  className="w-full h-9 bg-black/50 border border-white/15 px-3 text-sm"
                                />
                                <button
                                  type="button"
                                  disabled={busy}
                                  onClick={() => confirmStock(detail.order?.id || selected.order_id, true)}
                                  className="w-full h-10 border border-cyan-electric/50 text-cyan-electric font-mono-tech text-[10px] uppercase"
                                >
                                  Confirm order stock in queue
                                </button>
                              </div>
                            )}
                        </>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {tab !== "catalog" && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 min-h-[520px]">
          <div className="lg:col-span-5 border border-white/10 bg-obsidian/40 overflow-hidden flex flex-col max-h-[70vh]">
            <div className="px-4 py-3 border-b border-white/10 font-mono-tech text-[10px] uppercase text-white/45">
              {list.length} items
            </div>
            <div className="flex-1 overflow-y-auto divide-y divide-white/5">
              {loading && !dash && (
                <div className="p-8 flex justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-cyan-electric" />
                </div>
              )}
              {!loading && list.length === 0 && (
                <div className="p-8 text-sm text-white/40">No items in this queue.</div>
              )}
              {list.map((row) => (
                <button
                  key={row.session_id || row.order_id}
                  type="button"
                  onClick={() => setSelected(row)}
                  className={`w-full text-left p-4 hover:bg-white/5 transition-colors ${
                    selected?.session_id === row.session_id ? "bg-cyan-electric/5 border-l-2 border-cyan-electric" : ""
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-display text-base truncate">
                        {row.customer?.name || row.user?.name || "Customer"}
                      </div>
                      <div className="text-xs text-white/45 truncate">
                        {row.part_name || row.customer?.part_query || row.listing_sku || "-"}
                      </div>
                      <div className="mt-1">
                        <IntentBadge intent={row.intent} />
                      </div>
                    </div>
                    {row.buyer_ready && (
                      <span className="shrink-0 h-2 w-2 rounded-full bg-mclaren animate-pulse mt-1" title="Buyer ready" />
                    )}
                  </div>
                  <div className="mt-2 font-mono-tech text-[9px] uppercase text-white/30">
                    {row.stage?.replace(/_/g, " ")} · {row.updated_at?.slice(0, 16) || row.order?.created_at?.slice(0, 16)}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7 border border-white/10 bg-obsidian/60 flex flex-col max-h-[70vh]">
            {!selected && (
              <div className="flex-1 flex items-center justify-center text-white/35 text-sm p-8 text-center">
                Select a lead to view customer details, agent chat history, and confirm stock.
              </div>
            )}
            {selected && (
              <>
                <div className="px-5 py-4 border-b border-white/10">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="font-display text-xl">
                        {detail?.customer_full?.name || selected.customer?.name || selected.user?.name}
                      </div>
                      <div className="text-sm text-white/50">{selected.user?.email}</div>
                      <IntentBadge intent={selected.intent} />
                    </div>
                    {selected.order_id || detail?.order?.id ? (
                      <div className="text-right font-mono-tech text-[10px] uppercase text-white/40">
                        Order {selected.order_id || detail?.order?.id}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                  {detailLoading && (
                    <div className="flex justify-center py-8">
                      <Loader2 className="h-5 w-5 animate-spin text-cyan-electric" />
                    </div>
                  )}

                  {detail && (
                    <>
                      <div className="grid sm:grid-cols-2 gap-3">
                        <div className="border border-white/10 p-3">
                          <div className="font-mono-tech text-[9px] uppercase text-white/40 mb-1">Phone</div>
                          <div className="text-sm">{detail.customer_full?.phone || "-"}</div>
                        </div>
                        <div className="border border-white/10 p-3">
                          <div className="font-mono-tech text-[9px] uppercase text-white/40 mb-1">VIN</div>
                          <div className="text-sm font-mono-tech">{detail.customer_full?.vin || "-"}</div>
                        </div>
                        <div className="border border-white/10 p-3 sm:col-span-2">
                          <div className="font-mono-tech text-[9px] uppercase text-white/40 mb-1">Part requested</div>
                          <div className="text-sm">{detail.customer_full?.part_query || detail.listing?.name || "-"}</div>
                        </div>
                      </div>

                      {detail.listing && (
                        <div className="border border-cyan-electric/30 p-4">
                          <div className="font-mono-tech text-[10px] uppercase text-cyan-electric">
                            {detail.listing.sku} · matched listing
                          </div>
                          <div className="font-display text-lg mt-1">{detail.listing.name}</div>
                          <div className="text-cyan-electric font-display text-xl mt-1">
                            ${detail.listing.price_usd || "quote"}
                          </div>
                          <div className="text-xs text-white/45 mt-1">{detail.listing.availability_label}</div>
                        </div>
                      )}

                      {(detail.order?.status === "pending_sales_review" || selected.stage === "pending_sales_review") && (
                        <div className="border border-mclaren/40 bg-mclaren/5 p-4 space-y-3">
                          <div className="font-mono-tech text-[10px] uppercase text-mclaren">Confirm stock</div>
                          <input
                            value={confirmNote}
                            onChange={(e) => setConfirmNote(e.target.value)}
                            placeholder="Note to customer (optional)"
                            className="w-full h-9 bg-black/50 border border-white/15 px-3 text-sm"
                          />
                          <div className="flex gap-2">
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => confirmStock(detail.order?.id || selected.order_id, true)}
                              className="flex-1 h-10 bg-cyan-electric text-void font-mono-tech text-[10px] uppercase flex items-center justify-center gap-1"
                            >
                              <CheckCircle2 className="h-4 w-4" /> In stock
                            </button>
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => confirmStock(detail.order?.id || selected.order_id, false)}
                              className="flex-1 h-10 border border-white/20 font-mono-tech text-[10px] uppercase flex items-center justify-center gap-1"
                            >
                              <XCircle className="h-4 w-4" /> Unavailable
                            </button>
                          </div>
                        </div>
                      )}

                      <div>
                        <div className="font-mono-tech text-[10px] uppercase text-white/40 mb-2">Agent chat</div>
                        <div className="space-y-2 max-h-48 overflow-y-auto border border-white/10 p-3 bg-black/30">
                          {(detail.messages || []).map((m, i) => (
                            <div
                              key={i}
                              className={`text-xs whitespace-pre-wrap ${
                                m.role === "user" ? "text-mclaren/90" : "text-white/70"
                              }`}
                            >
                              <span className="font-mono-tech text-[8px] uppercase text-white/30">
                                {m.role === "user" ? "customer" : m.from_sales ? "sales" : "agent"}:
                              </span>{" "}
                              {m.content}
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {selected.session_id && (
                  <form onSubmit={sendReply} className="shrink-0 border-t border-white/10 p-4 flex gap-2">
                    <input
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      placeholder="Reply to customer (appears in their agent chat)…"
                      className="flex-1 h-10 bg-black/50 border border-white/15 px-3 text-sm"
                    />
                    <button
                      type="submit"
                      disabled={busy || reply.trim().length < 2}
                      className="h-10 px-4 bg-mclaren text-void font-mono-tech text-[10px] uppercase"
                    >
                      Send
                    </button>
                  </form>
                )}
              </>
            )}
          </div>
        </div>
        )}
      </div>
    </ConsoleShell>
  );
}
